import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { ShopStatusBadge } from './ShopStatusBadge';
import apiService from '../../services/ApiService';
import type { Shop } from '../../types/buildExpansion';
import PageShell from '../ui/PageShell';
import { Button } from '../ui/Button';
import { EmptyState, CarIcon } from '../ui/EmptyState';
import PageBackLink from '../ui/PageBackLink';

type PortfolioRow = Record<string, unknown>;

const asString = (row: PortfolioRow, key: string): string | null => {
  const value = row[key];
  if (typeof value === 'string' && value.trim() !== '') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

const PortfolioCard: React.FC<{ row: PortfolioRow }> = ({ row }) => {
  const year = asString(row, 'year');
  const make = asString(row, 'make');
  const model = asString(row, 'model');
  const nickname = asString(row, 'nickname');
  const imageUrl = asString(row, 'image_url');
  const publicSlug = asString(row, 'public_slug');
  const ownerUsername = asString(row, 'owner_username');

  const vehicleName = [year, make, model].filter(Boolean).join(' ') || nickname || 'Build';
  const href = ownerUsername && publicSlug ? `/${ownerUsername}/${publicSlug}` : null;

  const inner = (
    <>
      {imageUrl ? (
        <img
          src={apiService.getFileUrl(imageUrl) || imageUrl}
          alt={vehicleName}
          className="w-full h-40 object-cover bg-[#0a0a0a]"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-40 bg-[#111]/80 flex items-center justify-center text-gray-600" aria-hidden>
          <CarIcon className="w-12 h-12" />
        </div>
      )}
      <div className="p-4">
        <p className="text-sm font-semibold text-white truncate">{vehicleName}</p>
        {nickname && vehicleName !== nickname ? (
          <p className="text-xs text-gray-400 truncate mt-0.5">“{nickname}”</p>
        ) : null}
        {ownerUsername ? <p className="text-xs text-gray-500 mt-1 truncate">@{ownerUsername}</p> : null}
      </div>
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="card-interactive block overflow-hidden cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 rounded-xl"
      >
        {inner}
      </Link>
    );
  }
  return <div className="card overflow-hidden">{inner}</div>;
};

/** Public shop profile: header with shop details plus a customer builds portfolio. */
export const ShopProfilePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [shop, setShop] = useState<Shop | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await apiService.getShopBySlug(slug);
        if (cancelled) return;
        setShop(result.shop);
        setPortfolio(result.portfolio);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load this shop.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <PageShell width="md">
        <div role="status" aria-busy="true">
          <span className="sr-only">Loading shop…</span>
          <div className="h-44 sm:h-56 rounded-xl bg-[#111]/80 animate-pulse motion-reduce:animate-none mb-6" aria-hidden />
          <div className="h-8 w-64 rounded bg-[#111] animate-pulse motion-reduce:animate-none mb-3" aria-hidden />
          <div className="h-4 w-full max-w-xl rounded bg-[#111]/60 animate-pulse motion-reduce:animate-none mb-8" aria-hidden />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-56 rounded-xl border border-white/[0.06] bg-[#0c0c0c]/70 animate-pulse motion-reduce:animate-none"
                aria-hidden
              />
            ))}
          </div>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell width="md">
        <div role="alert" className="alert-error mb-4">
          {error}
        </div>
        <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </PageShell>
    );
  }

  if (!shop) {
    return (
      <PageShell width="md">
        <EmptyState
          icon={<BuildingStorefrontIcon className="w-14 h-14" />}
          title="Shop not found"
          description="This shop does not exist or is no longer listed."
          action={
            <Link to="/shops" className="btn-primary">
              Browse shops
            </Link>
          }
        />
      </PageShell>
    );
  }

  const location = [shop.city, shop.state].filter(Boolean).join(', ');
  const coverUrl = shop.cover_image_path
    ? apiService.getFileUrl(shop.cover_image_path) || shop.cover_image_path
    : null;
  const logoUrl = shop.logo_path ? apiService.getFileUrl(shop.logo_path) || shop.logo_path : null;
  const trust = shop.trust_label ?? (shop.is_verified ? 'verified' : shop.is_community_verified ? 'community_verified' : 'listed');

  return (
    <PageShell width="md">
      <PageBackLink label="Back to Shops" to="/shops" className="mb-6" />
      <header className="mb-10">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt=""
            className="w-full h-44 sm:h-56 object-cover rounded-xl border border-white/[0.06] bg-[#0a0a0a] mb-[-2.5rem]"
          />
        ) : (
          <div className="w-full h-28 sm:h-36 rounded-xl bg-gradient-brand-soft border border-white/[0.06] mb-[-2.5rem]" aria-hidden />
        )}
        <div className="relative px-4 sm:px-6">
          <div className="flex items-end gap-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover border-2 border-white/[0.06] bg-[#0a0a0a] shadow-elevated shrink-0"
              />
            ) : (
              <div
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-gradient-brand-tile border-2 border-white/[0.06] shadow-elevated flex items-center justify-center text-2xl font-bold text-white shrink-0"
                aria-hidden
              >
                {(shop.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 pb-1">
              <p className="kicker mb-2">Shop</p>
              <h1 className="heading-display flex items-center gap-2 flex-wrap">
                <span className="min-w-0 break-words">{shop.name}</span>
                {(trust === 'verified' || trust === 'community_verified') && (
                  <ShopStatusBadge trustLabel={trust} />
                )}
              </h1>
              {location ? (
                <p className="text-sm text-gray-400 mt-1.5 flex items-center gap-1.5">
                  <MapPinIcon className="w-4 h-4 shrink-0" aria-hidden />
                  {location}
                </p>
              ) : null}
            </div>
          </div>

          {shop.description ? (
            <p className="text-sm text-gray-300 leading-relaxed max-w-2xl mt-4">{shop.description}</p>
          ) : null}

          {shop.services.length > 0 && (
            <ul className="flex flex-wrap gap-1.5 mt-4" aria-label="Services">
              {shop.services.map((service) => (
                <li key={service} className="badge bg-[#111] text-gray-300 border border-white/[0.08]">
                  {service}
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-5">
            {shop.website_url ? (
              <a
                href={shop.website_url}
                rel="noopener noreferrer"
                target="_blank"
                className="btn-secondary btn-sm inline-flex"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden />
                Website
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            ) : null}
            {shop.contact_url ? (
              <a
                href={shop.contact_url}
                rel="noopener noreferrer"
                target="_blank"
                className="btn-ghost btn-sm inline-flex"
              >
                Contact
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            ) : null}
          </div>
        </div>
      </header>

      <section aria-labelledby="shop-portfolio-heading">
        <h2 id="shop-portfolio-heading" className="heading-section mb-4">
          Customer builds
          {portfolio.length > 0 ? (
            <span className="ml-2 text-sm font-normal text-gray-500">
              {portfolio.length === 1 ? '1 build' : `${portfolio.length} builds`}
            </span>
          ) : null}
        </h2>
        {portfolio.length === 0 ? (
          <EmptyState
            variant="dense"
            icon={<CarIcon className="w-12 h-12" />}
            title="No customer builds yet"
            description="Builds that credit this shop as a contributor will show up here."
          />
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 list-none">
            {portfolio.map((row, i) => (
              <li key={asString(row, 'id') ?? asString(row, 'public_slug') ?? i}>
                <PortfolioCard row={row} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </PageShell>
  );
};

export default ShopProfilePage;
